import { forwardRef } from "react";
import { Languages } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * LanguageBadge — UZ/RU/EN flag/icon ko'rinishida til indikatori.
 * Manba: docs/architecture/chatbot-ai-pipeline.md § 4 (multi-language ADR 0023)
 */

export type SupportedLanguage = "uz" | "ru" | "en" | (string & {});

interface LanguageMeta {
  label: string;
  emoji: string;
  fullName: string;
  cls: string;
}

const META: Record<string, LanguageMeta> = {
  uz: {
    label: "UZ",
    emoji: "🇺🇿",
    fullName: "O'zbekcha",
    cls: "bg-[var(--lang-uz-bg)] text-[var(--lang-uz-fg)] border-[var(--lang-uz-border)]",
  },
  ru: {
    label: "RU",
    emoji: "🇷🇺",
    fullName: "Русский",
    cls: "bg-[var(--lang-ru-bg)] text-[var(--lang-ru-fg)] border-[var(--lang-ru-border)]",
  },
  en: {
    label: "EN",
    emoji: "🇬🇧",
    fullName: "English",
    cls: "bg-[var(--lang-en-bg)] text-[var(--lang-en-fg)] border-[var(--lang-en-border)]",
  },
};

const FALLBACK: LanguageMeta = {
  label: "??",
  emoji: "🌐",
  fullName: "Aniqlanmagan",
  cls: "bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-subtle)]",
};

const sizeMap = {
  sm: "h-4 px-1.5 text-[10px] gap-1",
  md: "h-5 px-2 text-[11px] gap-1.5",
  lg: "h-6 px-2.5 text-xs gap-1.5",
} as const;

export interface LanguageBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  language: SupportedLanguage | null | undefined;
  size?: "sm" | "md" | "lg";
  /** Emoji bayroq o'rniga `Languages` ikonkasi */
  noEmoji?: boolean;
  /** Faqat label (UZ/RU/EN) */
  compact?: boolean;
}

export const LanguageBadge = forwardRef<HTMLSpanElement, LanguageBadgeProps>(
  (
    { language, size = "md", noEmoji = false, compact = true, className, ...props },
    ref,
  ) => {
    const key = (language ?? "").toString().toLowerCase();
    const meta = META[key] ?? FALLBACK;
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center rounded-full border font-medium leading-none whitespace-nowrap font-mono",
          sizeMap[size],
          meta.cls,
          className,
        )}
        title={meta.fullName}
        {...props}
      >
        {noEmoji ? (
          <Languages className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <span aria-hidden className="leading-none">
            {meta.emoji}
          </span>
        )}
        <span>{compact ? meta.label : meta.fullName}</span>
      </span>
    );
  },
);
LanguageBadge.displayName = "LanguageBadge";
